/**
 * 后端菜单数据 <-> vue-router 路由 的转换与本地缓存
 * 这个文件是「叶子模块」：不 import 项目内其他模块，避免循环依赖
 */

// 菜单在 localStorage 里的 key
export const MENU_KEY = 'order_list';

/**
 * 读取本地缓存的后端菜单（登录接口返回的 order_list）
 * @returns {Array} 菜单数组，异常时返回 []
 */
export function readMenu() {
  try {
    const raw = localStorage.getItem(MENU_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

/**
 * 写入菜单缓存
 * @param {Array} menuList 后端返回的 order_list
 */
export function writeMenu(menuList) {
  const list = Array.isArray(menuList) ? menuList : [];
  localStorage.setItem(MENU_KEY, JSON.stringify(list));
  return list;
}

/** 清除菜单缓存（退出登录时用） */
export function clearMenu() {
  localStorage.removeItem(MENU_KEY);
}

/**
 * 统一路径格式：保证以 / 开头
 * 后端存的可能是 "order/list"，也可能是 "/order/list"
 */
function normalizePath(path) {
  if (typeof path !== 'string' || !path) return '';
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * 把后端菜单树转换成 vue-router 路由表
 * 约定：二级菜单的 path 既是 URL，也对应 src/views{path}.vue 文件
 * 例：path = "/order/list"  ->  @/views/order/list.vue
 *
 * @param {Array} menuList 后端菜单
 * @param {Object} Layout  布局组件（外层壳子）
 * @returns {Array} vue-router 路由数组
 */
export function generateDynamicRoutes(menuList, Layout) {
  if (!Array.isArray(menuList) || !Layout) return [];

  const routes = [];

  menuList.forEach(group => {
    const children = Array.isArray(group.children) ? group.children : [];
    // 没有二级页面的分组，注册了也打不开，直接跳过
    if (!children.length) return;

    const childRoutes = [];
    children.forEach(item => {
      const path = normalizePath(item.path);
      if (!path) return;
      childRoutes.push({
        path,
        name: item.name || path,
        component: () => import(/* webpackChunkName: "page" */ `@/views${path}.vue`),
      });
    });

    if (!childRoutes.length) return;

    routes.push({
      path: normalizePath(group.path) || `/${group.id || group.name || routes.length}`,
      component: Layout,
      // 直接访问一级菜单时，落到它的第一个二级页面
      redirect: childRoutes[0].path,
      children: childRoutes,
    });
  });

  return routes;
}

/**
 * 递归收集路由树里所有可访问的页面绝对路径（用于校验菜单是否指向真实页面）
 * @param {Array} routes 路由数组
 * @param {String} parentPath 父级路径
 * @returns {Array<String>} 绝对路径数组
 */
export function collectAccessiblePaths(routes, parentPath = '') {
  let result = [];
  if (!Array.isArray(routes)) return result;

  routes.forEach(route => {
    let currentPath = route.path || '';
    if (!currentPath.startsWith('/')) {
      currentPath = parentPath
        ? `${parentPath.replace(/\/$/, '')}/${currentPath}`
        : `/${currentPath}`;
    }

    const hasChildren = Array.isArray(route.children) && route.children.length > 0;

    if (hasChildren) {
      result = result.concat(collectAccessiblePaths(route.children, currentPath));
      if (typeof route.redirect === 'string') result.push(route.redirect);
    } else if (currentPath) {
      result.push(currentPath);
    }
  });

  return result;
}



// WEBPACK FOOTER //
// ./src/router/menu.js